import { type Grid, type Point, inBounds, isWalkable } from './grid.js';

/**
 * A* по сетке, восемь направлений, без срезания углов:
 * пацан не проходит между двумя гаражами по диагонали.
 * Стоимость шага — 1 по прямой, √2 по диагонали; из неё считаются игровые минуты.
 */

const STRAIGHT = 1;
const DIAGONAL = Math.SQRT2;

const NEIGHBOURS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, STRAIGHT],
  [-1, 0, STRAIGHT],
  [0, 1, STRAIGHT],
  [0, -1, STRAIGHT],
  [1, 1, DIAGONAL],
  [1, -1, DIAGONAL],
  [-1, 1, DIAGONAL],
  [-1, -1, DIAGONAL],
];

export interface PathResult {
  /** Клетки маршрута без стартовой; пустой массив — идти некуда. */
  points: Point[];
  /** Сумма стоимости шагов, из неё считаются минуты. */
  cost: number;
}

/** Восьминаправленная эвристика: не переоценивает, поэтому путь оптимальный. */
function heuristic(ax: number, ay: number, bx: number, by: number): number {
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return STRAIGHT * (dx + dy) + (DIAGONAL - 2 * STRAIGHT) * Math.min(dx, dy);
}

/** Двоичная куча вместо сортировки массива — на 48×48 разница уже заметна. */
class MinHeap {
  private readonly items: number[] = [];
  private readonly keys: Float64Array;

  constructor(capacity: number) {
    this.keys = new Float64Array(capacity);
  }

  get size(): number {
    return this.items.length;
  }

  push(node: number, priority: number): void {
    this.keys[node] = priority;
    this.items.push(node);
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.keys[this.items[parent]!]! <= this.keys[this.items[i]!]!) break;
      [this.items[parent], this.items[i]] = [this.items[i]!, this.items[parent]!];
      i = parent;
    }
  }

  pop(): number | undefined {
    const top = this.items[0];
    const last = this.items.pop();
    if (this.items.length > 0 && last !== undefined) {
      this.items[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let best = i;
        if (l < this.items.length && this.keys[this.items[l]!]! < this.keys[this.items[best]!]!) best = l;
        if (r < this.items.length && this.keys[this.items[r]!]! < this.keys[this.items[best]!]!) best = r;
        if (best === i) break;
        [this.items[best], this.items[i]] = [this.items[i]!, this.items[best]!];
        i = best;
      }
    }
    return top;
  }
}

export function findPath(grid: Grid, from: Point, to: Point): PathResult {
  const empty: PathResult = { points: [], cost: 0 };
  if (!inBounds(grid, from.x, from.y) || !inBounds(grid, to.x, to.y)) return empty;
  if (!isWalkable(grid, to.x, to.y)) return empty;
  if (from.x === to.x && from.y === to.y) return empty;

  const size = grid.width * grid.height;
  const start = from.y * grid.width + from.x;
  const goal = to.y * grid.width + to.x;

  const gScore = new Float64Array(size).fill(Infinity);
  const cameFrom = new Int32Array(size).fill(-1);
  const closed = new Uint8Array(size);
  const open = new MinHeap(size);

  gScore[start] = 0;
  open.push(start, heuristic(from.x, from.y, to.x, to.y));

  while (open.size > 0) {
    const current = open.pop();
    if (current === undefined) break;
    if (current === goal) return rebuild(grid, cameFrom, goal, gScore[goal]!);
    if (closed[current] === 1) continue;
    closed[current] = 1;

    const cx = current % grid.width;
    const cy = (current - cx) / grid.width;

    for (const [dx, dy, stepCost] of NEIGHBOURS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!isWalkable(grid, nx, ny)) continue;
      // Углы не срезаем: обе смежные клетки должны быть свободны.
      if (dx !== 0 && dy !== 0 && (!isWalkable(grid, cx + dx, cy) || !isWalkable(grid, cx, cy + dy))) continue;

      const neighbour = ny * grid.width + nx;
      if (closed[neighbour] === 1) continue;

      const tentative = gScore[current]! + stepCost;
      if (tentative < gScore[neighbour]!) {
        gScore[neighbour] = tentative;
        cameFrom[neighbour] = current;
        open.push(neighbour, tentative + heuristic(nx, ny, to.x, to.y));
      }
    }
  }

  return empty;
}

function rebuild(grid: Grid, cameFrom: Int32Array, goal: number, cost: number): PathResult {
  const points: Point[] = [];
  let node = goal;
  while (node !== -1) {
    const x = node % grid.width;
    points.push({ x, y: (node - x) / grid.width });
    node = cameFrom[node]!;
  }
  points.pop(); // стартовая клетка в маршрут не входит
  points.reverse();
  return { points, cost };
}
